import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Clock, X, SpinnerGap } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useCurrency } from "../context/CurrencyContext";
import Footer from "../components/Footer";
import UpiPaymentForm from "../components/UpiPaymentForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  awaiting_payment: {
    Icon: Clock,
    color: "text-amber-600",
    bg: "bg-amber-50",
    title: "awaiting payment",
    msg: "your order is created. please complete the UPI payment and submit the UTR to confirm.",
  },
  verification_pending: {
    Icon: Clock,
    color: "text-blue-600",
    bg: "bg-blue-50",
    title: "payment verification pending",
    msg: "we've received your payment details. our team will verify and confirm your order shortly. you'll get an email update soon.",
  },
  confirmed: {
    Icon: Check,
    color: "text-green-600",
    bg: "bg-green-50",
    title: "order confirmed",
    msg: "your payment is verified and your order is confirmed. thanks for supporting vandy's!",
  },
  rejected: {
    Icon: X,
    color: "text-[#E01A23]",
    bg: "bg-red-50",
    title: "payment rejected",
    msg: "we couldn't verify this payment. please reach out to hello@vandys.art and we'll sort it out.",
  },
};

export default function OrderStatusPage() {
  const { orderId } = useParams();
  const { formatPrice } = useCurrency();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrder(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Order not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll every 10s if verification pending so customer sees real-time status
    const t = setInterval(() => {
      load();
    }, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (loading && !order) {
    return (
      <div className="pt-20 md:pt-24 min-h-screen flex items-center justify-center">
        <SpinnerGap size={32} className="animate-spin text-[#E01A23]" />
      </div>
    );
  }

  if (err || !order) {
    return (
      <div className="pt-20 md:pt-24 min-h-screen text-center px-6 py-16">
        <h1 className="text-3xl text-[#1A1A1A] mb-3" style={{ fontFamily: "'Lazy Dog', cursive" }}>order not found</h1>
        <p className="text-sm text-[#3a3a3a]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>{err}</p>
        <Link to="/shop" className="inline-block mt-6">
          <Button className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-11 px-8 text-sm">go to shop</Button>
        </Link>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.verification_pending;
  const { Icon } = cfg;

  return (
    <div data-testid="order-status-page" className="pt-20 md:pt-24 min-h-screen">
      <div className="max-w-[800px] mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-12">
        <div className={`${cfg.bg} rounded-2xl p-8 md:p-10 mb-6 text-center`}>
          <Icon size={56} weight="duotone" className={`mx-auto mb-4 ${cfg.color}`} />
          <h1 className="text-3xl md:text-4xl text-[#1A1A1A] mb-2" style={{ fontFamily: "'Lazy Dog', cursive" }} data-testid="order-status-title">
            {cfg.title}
          </h1>
          <p className="text-sm text-[#3a3a3a] max-w-md mx-auto" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
            {cfg.msg}
          </p>
          {order.status === "rejected" && order.rejection_reason && (
            <p className="text-xs text-[#E01A23] mt-3" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
              reason: {order.rejection_reason}
            </p>
          )}
        </div>

        <div className="bg-[#EBD988] rounded-2xl p-6 md:p-8 mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs text-[#555] uppercase tracking-wider" style={{ fontFamily: "'Courier New', Courier, monospace" }}>order number</span>
            <span className="text-sm text-[#1A1A1A]" data-testid="order-number" style={{ fontFamily: "'Courier New', Courier, monospace" }}>{order.order_number}</span>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs text-[#555] uppercase tracking-wider" style={{ fontFamily: "'Courier New', Courier, monospace" }}>amount</span>
            <span className="text-lg text-[#E01A23]" data-testid="order-amount" style={{ fontFamily: "'Lazy Dog', cursive" }}>{formatPrice(order.amount)}</span>
          </div>
          {order.utr_number && (
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-[#555] uppercase tracking-wider" style={{ fontFamily: "'Courier New', Courier, monospace" }}>UTR</span>
              <span className="text-sm text-[#1A1A1A]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>{order.utr_number}</span>
            </div>
          )}

          <div className="border-t border-[#E01A23]/10 pt-4 mt-4">
            <h3 className="text-sm text-[#1A1A1A] mb-3" style={{ fontFamily: "'Lazy Dog', cursive" }}>items</h3>
            <div className="space-y-2">
              {(order.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between text-xs" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                  <span className="text-[#1A1A1A] truncate">{it.name} × {it.quantity}</span>
                  <span className="text-[#E01A23] whitespace-nowrap">{formatPrice(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {order.status === "awaiting_payment" && (
          <div className="mb-6">
            <UpiPaymentForm
              order={{ ...order, order_id: order.id, order_number: order.order_number }}
              onSubmitted={load}
            />
          </div>
        )}

        <Link to="/shop" className="block mt-3">
          <Button variant="outline" className="w-full rounded-full h-11 text-sm border-[#E01A23] text-[#E01A23] hover:bg-[#E01A23] hover:text-white">continue shopping</Button>
        </Link>
      </div>
      <Footer />
    </div>
  );
}
